import type { AnchorHTMLAttributes, ReactNode, Ref } from 'react'
import type { AppLinkProps } from '@/components/elementary/AppLink/types'
import { Google } from '@/components/icons/social/Google'
import { openCookiePreferencesModal } from '@/lib/analytics/cookie-consent'

export const getLinkExceptions = (
	options: {
		href: AppLinkProps['href']
		ref: Ref<HTMLAnchorElement>
		linkClassName: string
		props: AnchorHTMLAttributes<HTMLAnchorElement>
	},
	renderContent: () => ReactNode,
) => {
	const { href, ref, linkClassName, props } = options

	if (typeof href === 'string' && props.download) {
		return (
			<a href={href} ref={ref} download className={linkClassName} {...props}>
				{renderContent()}
			</a>
		)
	}

	if (typeof href === 'string' && href === '#intercom') {
		return (
			<a
				href={href}
				ref={ref}
				onClick={(e) => {
					e.preventDefault()
					if (window?.Intercom) {
						window.Intercom('show')
					} else {
						console.warn('Intercom is not initialized')
					}
				}}
				className={linkClassName}
				{...props}
			>
				{renderContent()}
			</a>
		)
	}

	if (typeof href === 'string' && href === '#cookie-settings') {
		return (
			<a
				href={href}
				ref={ref}
				onClick={(e) => {
					e.preventDefault()
					openCookiePreferencesModal()
				}}
				className={linkClassName}
				{...props}
			>
				{renderContent()}
			</a>
		)
	}

	return undefined
}

export const renderContent = (
	props: Pick<AppLinkProps, 'variant' | 'iconLeft' | 'iconRight' | 'label' | 'children'>,
) => (
	<>
		{props.variant === 'google' && (
			<>
				<div className="absolute rounded-l-full bg-neutral-light-100 aspect-square left-0 top-0 h-full flex items-center justify-center">
					<Google className="size-12" />
				</div>
				<span className="sm:flex hidden aspect-square h-full -mr-1" />
			</>
		)}
		{props.iconLeft && (
			<div className="flex items-center gap-1">
				<span className="mr-0 group-hover/app-link:mr-1 group-hover/image-link:mr-1 transition-[margin] duration-200">
					{props.iconLeft}
				</span>
			</div>
		)}
		{props.children}
		{(props.label || props.iconRight) && (
			<div className="flex items-center gap-1">
				{props.label && <div className="flex justify-between">{props.label}</div>}
				{props.iconRight && (
					<span className="ml-0 group-hover/app-link:ml-1 group-hover/image-link:ml-1 transition-[margin] duration-200">
						{props.iconRight}
					</span>
				)}
			</div>
		)}
	</>
)

const SOCIAL_LOGIN_URL = 'https://app.lokalise.com/login/social' as const

const isSignUpWithGoogleLink = (href: AppLinkProps['href'], label: AppLinkProps['label']) => {
	return href.toString().startsWith(SOCIAL_LOGIN_URL) && label?.toLowerCase().includes('google')
}

export const determineLinkVariant = (
	href: AppLinkProps['href'],
	label: AppLinkProps['label'],
	variant: AppLinkProps['variant'],
) => (isSignUpWithGoogleLink(href, label) ? 'google' : variant || 'primaryLink')
